import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";
import { Eye, EyeOff, Loader2 } from "lucide-react";

function describe(detail) {
  if (!detail) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const next = new URLSearchParams(loc.search).get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenido");
      nav(next, { replace: true });
    } catch (err) {
      toast.error(describe(err?.response?.data?.detail) || "No se pudo iniciar sesión");
    } finally { setBusy(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Ingresa tu correo"); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      toast.success("Si el correo existe, enviamos un enlace para restablecer tu contraseña.");
      setForgot(false);
    } catch (err) {
      toast.error(describe(err?.response?.data?.detail) || "No se pudo enviar el correo");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7] p-6" data-testid="login-page">
      <div className="w-full max-w-md bg-white border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="bg-[#F4F2EC] p-6 border-b border-border text-center">
          <img src={LOGO_URL} alt="Madera Blanca Studio" className="h-14 mx-auto mb-2 object-contain" />
          <div className="uppercase-label text-[#8B5A33]">Madera Blanca Studio</div>
        </div>
        <div className="p-8">
          {!forgot ? (
            <>
              <h1 className="font-display text-3xl mb-6">Inicia sesión</h1>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Correo</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@dominio.com" autoFocus required data-testid="login-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pw">Contraseña</Label>
                  <div className="relative">
                    <Input id="pw" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground p-1" tabIndex={-1} aria-label="Mostrar contraseña">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#8B5A33] hover:bg-[#6D4525] text-white" disabled={busy} data-testid="login-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Entrar
                </Button>
                <button type="button" className="w-full text-sm text-[#8B5A33] hover:underline mt-2" onClick={() => { setForgot(true); setForgotEmail(email); }} data-testid="login-forgot">
                  ¿Olvidaste tu contraseña?
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl mb-2">Recuperar contraseña</h1>
              <p className="text-sm text-muted-foreground mb-5">Te enviaremos un enlace para restablecer tu contraseña.</p>
              <form onSubmit={sendReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fe">Correo</Label>
                  <Input id="fe" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email" />
                </div>
                <Button type="submit" className="w-full bg-[#8B5A33] hover:bg-[#6D4525] text-white" disabled={busy} data-testid="forgot-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Enviar enlace
                </Button>
                <button type="button" className="w-full text-sm text-muted-foreground hover:text-foreground mt-1" onClick={() => setForgot(false)}>Volver al inicio de sesión</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
